import { Select } from "../../primitives/Select";
import { Input } from "../../primitives/Input";
import { Search, X } from "lucide-react";
import type { PromotionStatus, PromotionType, PromotionScope } from "../../../api/catalog";
import type { PriceBook } from "../../../api/catalog";

export type PeriodFilter = "today" | "7days" | "month" | "custom";

export type PromotionsFiltersState = {
  status: PromotionStatus | "expired" | "";
  period: PeriodFilter;
  type: PromotionType | "";
  scope: PromotionScope | "";
  priceBookIds: string[];
  search: string;
};

const STATUS_OPTIONS = [
  { value: "", label: "Tous les statuts" },
  { value: "draft", label: "Brouillon" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Pausée" },
  { value: "expired", label: "Expirée" },
  { value: "archived", label: "Archivée" },
];

const PERIOD_OPTIONS = [
  { value: "today", label: "Aujourd'hui" },
  { value: "7days", label: "7 jours" },
  { value: "month", label: "Mois" },
  { value: "custom", label: "Personnalisé" },
];

const TYPE_OPTIONS = [
  { value: "", label: "Tous les types" },
  { value: "percentage", label: "Pourcentage" },
  { value: "fixed_price", label: "Prix fixe" },
];

const SCOPE_OPTIONS = [
  { value: "", label: "Tous" },
  { value: "all_products", label: "Tous les produits" },
  { value: "selected_products", label: "Produits sélectionnés" },
];

type PromotionsFiltersProps = {
  filters: PromotionsFiltersState;
  onFiltersChange: (f: PromotionsFiltersState) => void;
  priceBooks: PriceBook[];
};

export function PromotionsFilters({
  filters,
  onFiltersChange,
  priceBooks,
}: PromotionsFiltersProps) {
  const update = (patch: Partial<PromotionsFiltersState>) => {
    onFiltersChange({ ...filters, ...patch });
  };

  const togglePriceBook = (id: string) => {
    const next = filters.priceBookIds.includes(id)
      ? filters.priceBookIds.filter((x) => x !== id)
      : [...filters.priceBookIds, id];
    update({ priceBookIds: next });
  };

  return (
    <div className="flex flex-wrap items-center gap-3 p-3 border-b border-border bg-surface-muted/50">
      <Select
        label="Statut"
        hideLabel
        options={STATUS_OPTIONS.map((o) => o.label)}
        value={STATUS_OPTIONS.find((o) => o.value === filters.status)?.label ?? "Tous les statuts"}
        onChange={(label) => {
          const opt = STATUS_OPTIONS.find((o) => o.label === label);
          update({ status: (opt?.value ?? "") as PromotionsFiltersState["status"] });
        }}
      />
      <Select
        label="Période"
        hideLabel
        options={PERIOD_OPTIONS.map((o) => o.label)}
        value={PERIOD_OPTIONS.find((o) => o.value === filters.period)?.label ?? "Aujourd'hui"}
        onChange={(label) => {
          const opt = PERIOD_OPTIONS.find((o) => o.label === label);
          update({ period: (opt?.value ?? "today") as PeriodFilter });
        }}
      />
      <Select
        label="Type"
        hideLabel
        options={TYPE_OPTIONS.map((o) => o.label)}
        value={TYPE_OPTIONS.find((o) => o.value === filters.type)?.label ?? "Tous les types"}
        onChange={(label) => {
          const opt = TYPE_OPTIONS.find((o) => o.label === label);
          update({ type: (opt?.value ?? "") as PromotionType | "" });
        }}
      />
      <Select
        label="Applique sur"
        hideLabel
        options={SCOPE_OPTIONS.map((o) => o.label)}
        value={SCOPE_OPTIONS.find((o) => o.value === filters.scope)?.label ?? "Tous"}
        onChange={(label) => {
          const opt = SCOPE_OPTIONS.find((o) => o.label === label);
          update({ scope: (opt?.value ?? "") as PromotionScope | "" });
        }}
      />
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-muted">PriceBooks:</span>
        {priceBooks.map((pb) => (
          <button
            key={pb.id}
            type="button"
            onClick={() => togglePriceBook(pb.id)}
            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium border transition-colors ${
              filters.priceBookIds.includes(pb.id)
                ? "bg-primary/15 border-primary/40 text-primary"
                : "bg-surface border-border text-muted hover:border-primary/30"
            }`}
          >
            {pb.name}
          </button>
        ))}
      </div>
      <div className="flex-1 min-w-[180px] flex items-center gap-2">
        <Search size={16} className="text-muted shrink-0" />
        <input
          type="text"
          placeholder="Nom, code..."
          value={filters.search}
          onChange={(e) => update({ search: e.target.value })}
          className="flex-1 min-w-0 rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
        {filters.search && (
          <button
            type="button"
            onClick={() => update({ search: "" })}
            className="p-1 rounded text-muted hover:bg-surface-muted"
            aria-label="Effacer"
          >
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
